import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Store, ShoppingBag, IndianRupee, Users, Truck, CheckCircle, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';

const businessTypes = [
  "Retail Store",
  "Restaurant / Food",
  "Fashion & Clothing",
  "Electronics",
  "Grocery",
  "Home & Living",
  "Health & Beauty",
  "Sports & Fitness",
  "Books & Stationery",
  "Other"
];

export default function LandingPage() {
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_number: '',
    email: '',
    business_type: '',
    business_name: '',
    upi_id: ''
  });
  const [signupResult, setSignupResult] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    // Validate UPI ID
    if (!formData.upi_id.includes('@')) {
      toast.error('Please enter a valid UPI ID (e.g., name@bank)');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.sellerSignup({
        ...formData,
        terms_accepted: termsAccepted
      });
      setSignupResult(response.data);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Store, title: 'Your Own Shop', description: 'Get your personalized storefront URL to share with customers' },
    { icon: ShoppingBag, title: 'Easy Catalog', description: 'Add products with images, categories, and pricing in minutes' },
    { icon: IndianRupee, title: 'UPI Payments', description: 'Accept payments via UPI QR codes - no integration hassle' },
    { icon: Truck, title: 'Order Management', description: 'Accept or reject orders with one click, notify customers instantly' },
    { icon: Users, title: 'Customer Reach', description: 'Share your shop link on WhatsApp, social media, anywhere' },
    { icon: CheckCircle, title: 'Zero Commission', description: 'Keep 100% of your earnings - we charge no transaction fees' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store className="h-8 w-8 text-primary" />
            <span className="font-heading text-xl font-bold">My Market Place</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/seller/login">
              <Button variant="ghost" data-testid="seller-login-btn">Seller Login</Button>
            </Link>
            <Button 
              onClick={() => setIsSignupOpen(true)} 
              className="rounded-full px-6 btn-glow"
              data-testid="get-started-btn"
            >
              Start Selling
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="gradient-hero pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-6 space-y-6 animate-slide-up">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Your Business, <br />
                <span className="text-primary">Your Online Shop</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                Create your free online storefront in minutes. Sell products, accept UPI payments, and grow your business - all without any technical knowledge.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 btn-glow text-lg"
                  onClick={() => setIsSignupOpen(true)}
                  data-testid="hero-cta-btn"
                >
                  Start Free Today <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-secondary" />
                  <span>Free signup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-secondary" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-secondary" />
                  <span>India only</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-6 animate-slide-up stagger-2">
              <img 
                src="https://images.unsplash.com/photo-1722670448303-19827a3d1574?w=800&q=80" 
                alt="Indian shopkeeper using technology"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-card">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Sell Online
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Simple, powerful tools designed for Indian businesses
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="card-hover border-border/50"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="font-heading">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Start Selling in 3 Simple Steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Sign Up', description: 'Enter your business details and UPI ID. Get your shop link instantly.' },
              { step: '2', title: 'Add Products', description: 'Upload product images, set prices, and organize by categories.' },
              { step: '3', title: 'Share & Sell', description: 'Share your shop link. Customers order, you accept, they pay via UPI.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-16 w-16 rounded-full bg-primary text-primary-foreground text-2xl font-heading font-bold flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Online Business?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of Indian sellers who are growing their business online. It's free, fast, and simple.
          </p>
          <Button 
            size="lg" 
            variant="secondary" 
            className="rounded-full px-8 text-lg"
            onClick={() => setIsSignupOpen(true)}
            data-testid="cta-signup-btn"
          >
            Create Your Free Shop <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Store className="h-6 w-6 text-primary" />
                <span className="font-heading text-lg font-bold">My Market Place</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Empowering Indian businesses to sell online with ease. No technical skills required.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/seller/login" className="hover:text-primary transition-colors">Seller Login</Link></li>
               <li><a href="#" className="hover:text-primary transition-colors">support@itsmmp.com</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="https://itsmmp.com/Terms-and-Conditions.pdf" className="hover:text-primary transition-colors">Terms of Service</a></li>
                <li><a href="https://itsmmp.com/Privacy-Policy.pdf" className="hover:text-primary transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-muted-foreground text-sm">
            © 2026 ItsMMP.Com . All rights reserved. Made in India | An Vayodha Foundation Initiative
          </div>
        </div>
      </footer>

      {/* Signup Dialog */}
      <Dialog open={isSignupOpen} onOpenChange={setIsSignupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {!signupResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Create Your Shop</DialogTitle>
                <DialogDescription>
                  Fill in your business details to get started. It only takes 2 minutes!
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSignup} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_number">Contact Number *</Label>
                  <Input
                    id="contact_number"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    required
                    data-testid="signup-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_type">Business Type *</Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                  >
                    <SelectTrigger data-testid="signup-business-type-select">
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="Your shop or business name"
                    required
                    data-testid="signup-business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="upi_id">UPI ID *</Label>
                  <Input
                    id="upi_id"
                    name="upi_id"
                    value={formData.upi_id}
                    onChange={handleInputChange}
                    placeholder="yourname@upi"
                    required
                    data-testid="signup-upi-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This is where customers will pay. Format: name@bank
                  </p>
                </div>
                <div className="flex items-start space-x-2 pt-2">
                  <Checkbox 
                    id="terms" 
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    data-testid="signup-terms-checkbox"
                  />
                  <Label htmlFor="terms" className="text-sm leading-tight">
                    I accept the <a href="#" className="text-primary underline">Terms of Service</a> and <a href="#" className="text-primary underline">Privacy Policy</a>
                  </Label>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={loading}
                  data-testid="signup-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create My Shop'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-secondary flex items-center gap-2">
                  <CheckCircle className="h-6 w-6" /> Account Created!
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-muted rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Your Login Email</p>
                    <p className="font-medium">{signupResult.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Temporary Password</p>
                    <p className="font-medium font-mono bg-accent/20 px-2 py-1 rounded inline-block">
                      {signupResult.temp_password}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your Shop Link</p>
                    <p className="font-medium text-primary">/shop/{signupResult.shop_slug}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  We've also sent these details to your email. Please change your password after first login.
                </p>
                <div className="flex gap-3">
                  <Link to="/seller/login" className="flex-1">
                    <Button className="w-full rounded-full" data-testid="goto-login-btn">
                      Go to Login
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-full"
                    onClick={() => {
                      setSignupResult(null);
                      setIsSignupOpen(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
